import { supabase } from '@/integrations/supabase/client';
import { format, addDays } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { calculateGuardHours, logGuardDebug, isSpecialCaseGuard, shouldExcludeSchedule } from './hoursCalculationUtils';
import { formatHoursAndMinutes } from './dateUtils';

// Function to fetch teams
const fetchTeams = async (teamId: string | null = null) => {
  let query = supabase.from('teams').select('id, name');
  
  if (teamId) {
    query = query.eq('id', teamId);
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('Error fetching teams:', error);
    throw error;
  }
  
  return data || [];
};

// Function to fetch team members (guard IDs)
const fetchTeamMembers = async (teamId: string) => {
  const { data, error } = await supabase
    .from('team_members')
    .select('profile_id')
    .eq('team_id', teamId);
    
  if (error) {
    console.error('Error fetching team members:', error);
    return [];
  }
  
  return data.map(member => member.profile_id);
};

// Function to fetch guard profiles
const fetchGuardProfiles = async (guardIds: string[]) => {
  if (guardIds.length === 0) return [];
  
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email, first_name, last_name')
    .in('id', guardIds);
    
  if (error) {
    console.error('Error fetching guard profiles:', error);
    return [];
  }
  
  return data;
};

// Function to fetch team schedules - updated to only include shifts that START within the date range
const fetchTeamSchedules = async (teamId: string, startDate: Date, endDate: Date) => {
  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');
  
  // Add time component for more accurate filtering
  const startDateTime = `${startStr}T00:00:00`;
  const endDateTime = `${endStr}T23:59:59`;
  
  console.log(`[FETCH_TEAM_SCHEDULES] Fetching schedules for team ${teamId} that START between ${startDateTime} and ${endDateTime}`);
  
  const { data, error } = await supabase
    .from('team_schedules')
    .select('*')
    .eq('team_id', teamId)
    .gte('start_date', startDateTime)
    .lt('start_date', endDateTime);
    
  if (error) {
    console.error('Error fetching team schedules:', error);
    return [];
  }
  
  console.log(`[FETCH_TEAM_SCHEDULES] Found ${data?.length || 0} schedules for team ${teamId}`);
  return data || [];
};

// Process and calculate hours for all guards across teams
export const processGuardHours = async (
  teams: any[],
  weekStartDate: Date,
  weekEndDate: Date,
  fetchTeamMembers: (teamId: string) => Promise<string[]>,
  fetchGuardProfiles: (guardIds: string[]) => Promise<any[]>,
  fetchTeamSchedules: (teamId: string, start: Date, end: Date) => Promise<any[]>,
  includeLeaveHours: boolean = true
) => {
  // Create guard team maps (now using minutes for precision)
  const guardTotalMinutesMap = new Map<string, number>();
  const guardTeamMinutesMap = new Map<string, Map<string, number>>();
  const guardProfilesMap = new Map();
  
  console.log(`[HOURS PROCESSING] Processing hours for ${teams.length} teams. Include leave hours: ${includeLeaveHours}`);
  console.log(`[HOURS PROCESSING] Week start: ${weekStartDate.toISOString()}, Week end: ${weekEndDate.toISOString()}`);
  
  // Calculate hours for each guard in each team
  for (const team of teams) {
    console.log(`[HOURS PROCESSING] Processing team: ${team.name} (${team.id})`);
    const guardIds = await fetchTeamMembers(team.id);
    
    if (guardIds.length === 0) {
      console.log(`[HOURS PROCESSING] No guard members found for team ${team.name}`);
      continue;
    }
    
    console.log(`[HOURS PROCESSING] Found ${guardIds.length} guards in team ${team.name}`);
    
    // Fetch guard profiles for debugging
    const guardProfiles = await fetchGuardProfiles(guardIds);
    
    // Store guard profiles
    if (guardProfiles) {
      for (const profile of guardProfiles) {
        guardProfilesMap.set(profile.id, profile);
      }
    }

    // Fetch and process overlapping schedules
    const overlappingSchedules = await fetchTeamSchedules(team.id, weekStartDate, weekEndDate);
    console.log(`[HOURS PROCESSING] Found ${overlappingSchedules.length} schedules for team ${team.name}`);
    
    // Debug special guard info
    const specialGuardIds = guardIds.filter(id => {
      const guard = guardProfilesMap.get(id);
      return guard && (guard.full_name?.includes("ΚΩΝΣΤΑΝΤΙΝΟΥ") || guard.full_name?.includes("KONSTANTINOU"));
    });
    
    if (specialGuardIds.length > 0) {
      for (const guardId of specialGuardIds) {
        const guard = guardProfilesMap.get(guardId);
        if (guard) {
          console.log(`[SPECIAL GUARD] Processing schedules for ${guard.full_name} in team ${team.name}`);
          console.log(`  Total schedules in team: ${overlappingSchedules.length}`);
          console.log(`  includeLeaveHours setting: ${includeLeaveHours}`);
          
          // List all schedules for this guard
          const guardSchedules = overlappingSchedules.filter(
            s => s.assigned_guards && s.assigned_guards.includes(guardId)
          );
          
          console.log(`  ${guard.full_name} has ${guardSchedules.length} schedules in team ${team.name}`);
          guardSchedules.forEach(s => {
            const isLeave = shouldExcludeSchedule(s.title);
            const startDate = fromLocalISOString(s.start_date);
            const endDate = fromLocalISOString(s.end_date);
            let duration = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
            
            // Handle midnight end time
            if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
              duration = 24 - (startDate.getHours() + startDate.getMinutes() / 60);
            }
            
            console.log(`    - ${s.title} (${fromLocalISOString(s.start_date).toLocaleString()} - ${fromLocalISOString(s.end_date).toLocaleString()})`);
            console.log(`      Duration: ${duration.toFixed(2)}h, Is leave: ${isLeave}, Will be ${isLeave && !includeLeaveHours ? 'excluded' : 'included'}`);
          });
        }
      }
    }
    
    // Debug off-days/leave schedules to verify skipping
    if (overlappingSchedules && overlappingSchedules.length > 0) {
      const excludedSchedules = overlappingSchedules.filter(schedule => shouldExcludeSchedule(schedule.title));
      if (excludedSchedules.length > 0) {
        console.log(`[DEBUG] Found ${excludedSchedules.length} off-day/leave schedules in team ${team.name}:`, 
          excludedSchedules.map(s => s.title).join(', '));
        console.log(`  But includeLeaveHours=${includeLeaveHours}, so these will ${includeLeaveHours ? 'be included' : 'be excluded'} for total hours calculation`);
      }
    }
    
    // Calculate hours for each guard
    calculateGuardHours(
      team.id, 
      team.name, 
      overlappingSchedules, 
      guardProfilesMap, 
      guardTeamMinutesMap, 
      guardTotalMinutesMap,
      includeLeaveHours
    );
  }

  // Debug total minutes for all guards with substantial hours
  guardTotalMinutesMap.forEach((minutes, guardId) => {
    if (minutes > 30 * 60) {  // Only log guards with more than 30 hours (1800 minutes)
      const guard = guardProfilesMap.get(guardId);
      if (guard) {
        console.log(`[DEBUG HOURS] ${guard.full_name} - Total overall: ${formatHoursAndMinutes(minutes)}`);
        
        // Special detailed log for special case guards
        if (guard.full_name && (guard.full_name.includes("ΚΩΝΣΤΑΝΤΙΝΟΥ") || guard.full_name.includes("KONSTANTINOU"))) {
          console.log(`\n[DEBUG ${guard.full_name}] SPECIAL DEBUG - Total overall: ${formatHoursAndMinutes(minutes)}`);
          const guardTeamMinutes = guardTeamMinutesMap.get(guardId);
          if (guardTeamMinutes) {
            console.log(`Team-specific hours for ${guard.full_name}:`);
            guardTeamMinutes.forEach((teamMinutes, teamId) => {
              const team = teams.find(t => t.id === teamId);
              console.log(`  - ${team?.name || teamId}: ${formatHoursAndMinutes(teamMinutes)}`);
            });
          }
        }
      }
    }
  });

  return {
    guardTotalMinutesMap,
    guardTeamMinutesMap,
    guardProfilesMap
  };
};

// Generate alerts for guards with insufficient hours (less than 38 hours per week)
export const generateGuardHoursAlerts = async (weekStartDate: Date, teamId: string | null = null) => {
  const weekEndDate = addDays(weekStartDate, 6); // End of the week
  const formattedStartDate = format(weekStartDate, 'yyyy-MM-dd');
  
  console.log(`Generating guard hours alerts for week starting ${formattedStartDate}`);
  
  try {
    // Fetch teams (either specific team or all teams)
    const teams = await fetchTeams(teamId);
    
    if (teams.length === 0) {
      console.log('No teams found to process');
      return [];
    }
    
    // Process guard hours
    const { guardTotalMinutesMap, guardTeamMinutesMap, guardProfilesMap } = 
      await processGuardHours(
        teams, 
        weekStartDate, 
        weekEndDate,
        fetchTeamMembers,
        fetchGuardProfiles,
        fetchTeamSchedules,
        true // Include leave hours in calculation
      );
    
    // Generate alerts for guards with insufficient hours
    const alerts = [];
    const MINIMUM_HOURS = 38; // Minimum hours per week (updated from 35 to 38)
    
    // Check each guard's total minutes
    for (const [guardId, totalMinutes] of guardTotalMinutesMap.entries()) {
      const guard = guardProfilesMap.get(guardId);
      
      // Skip if no guard profile found
      if (!guard) continue;
      
      // Convert to hours for comparison with minimum
      const totalHours = totalMinutes / 60;
      
      // Only generate alerts for guards with less than minimum hours (now including 38)
      if (totalHours < MINIMUM_HOURS) {
        console.log(`[ALERT] Guard ${guard.full_name} has only ${formatHoursAndMinutes(totalMinutes)} scheduled for the week`);
        
        // Find which teams the guard is assigned to
        const guardTeamMinutes = guardTeamMinutesMap.get(guardId);
        
        if (guardTeamMinutes) {
          // Create an alert for each team the guard is in
          for (const [teamId, teamMinutes] of guardTeamMinutes.entries()) {
            const team = teams.find(t => t.id === teamId);
            
            if (team) {
              alerts.push({
                team_id: teamId,
                team_name: team.name,
                alert_type: 'INSUFFICIENT_HOURS',
                alert_message: `${guard.full_name} has only ${formatHoursAndMinutes(totalMinutes)} scheduled for the week (${formatHoursAndMinutes(teamMinutes)} in this team). A minimum of ${MINIMUM_HOURS} hours is required.`,
                guard_id: guardId,
                week_start_date: formattedStartDate,
                created_at: new Date().toISOString(),
                is_resolved: false,
                content: `${guard.full_name} has insufficient hours: ${formatHoursAndMinutes(totalMinutes)}`,
                other_team_hours: Object.fromEntries(
                  Array.from(guardTeamMinutes.entries()).map(([tid, mins]) => [tid, mins / 60])
                ),
                total_hours: totalHours
              });
            }
          }
        }
      }
    }
    
    console.log(`Generated ${alerts.length} guard hours alerts`);
    return alerts;
  } catch (error) {
    console.error('Error generating guard hours alerts:', error);
    throw error;
  }
};
